// src/services/teamService.js
// Centralized team helpers for Scrimly

import { db } from '@/firebase/firebase'
import {
  addDoc,
  setDoc,
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  collectionGroup,
  documentId,
  serverTimestamp,
  onSnapshot,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  writeBatch,
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { normalizeTeamRole, TEAM_ROLES } from '@/constants/roles'

/* ------------------------------ Team basics ------------------------------ */

export async function getTeam(teamId) {
  const snap = await getDoc(doc(db, 'teams', teamId))
  return snap.exists() ? { id: snap.id, ...snap.data() } : null
}

/**
 * Creates a real team and links it to the user (members subdoc).
 * Also marks team as "setupComplete: false" by default and (optionally) sets
 * users/{uid}.mustCompleteTeamId so guards can push them to Edit Team.
 *
 * Returns: { teamId }
 */
export async function createTeamForUser({
  uid,
  name,
  sport = null,            // primary sport (Soccer, Basketball, etc.)
  ageGroup = null,
  gender = null,
  skillLevel = null,
  address = null,
  location = null, // { lat, lng, name? } or null
  logoUrl = '',
  setPendingOnUser = true, // write users/{uid}.mustCompleteTeamId
  extraTeamFields = {},    // any extra fields to merge into team doc
}) {
  if (!uid) throw new Error('createTeamForUser: uid required')
  if (!name) throw new Error('createTeamForUser: name required')

  // Pre-generate the team id so all three writes can land atomically in
  // one batch. Previously these were three sequential awaits — if write
  // 2 (membership) or 3 (user link) failed after write 1, you got an
  // orphaned team or a team the owner couldn't access, with no rollback.
  // A batch makes team-creation all-or-nothing.
  const teamRef = doc(collection(db, 'teams'))
  const teamId = teamRef.id

  const batch = writeBatch(db)

  // 1) Team doc. Write both `sport` and `sportType` (canonical) from the
  //    same value so the two never drift — readers across the app use
  //    `sportType || sport`.
  batch.set(teamRef, {
    name,
    sport,
    sportType: sport,
    ageGroup,
    gender,
    skillLevel,
    address,
    location,
    placeId: extraTeamFields.placeId ?? null,
    logoUrl,
    coachId: uid, // legacy/owner field
    ownerId: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    profileStatus: 'incomplete',
    setupComplete: false,
    isArchived: false,
    archived: false,
    ...extraTeamFields,
  })

  // 2) Link membership (teams/{teamId}/members/{uid}) — roles we check elsewhere
  batch.set(doc(db, 'teams', teamId, 'members', uid), {
    role: 'owner',
    status: 'active',
    joinedAt: serverTimestamp(),
  }, { merge: true })

  // 3) Update user record:
  // - Always set currentTeam (handy for header/context, etc.)
  // - Optionally set mustCompleteTeamId to drive onboarding guard
  const userUpdate = {
    currentTeam: { id: teamId, name, logoUrl: logoUrl || '' },
    updatedAt: serverTimestamp(),
  }
  if (setPendingOnUser) {
    userUpdate.mustCompleteTeamId = teamId
    // keep legacy fields some flows might still read
    userUpdate.teamId = teamId
    userUpdate.onboardingStatus = 'team_created'
  }
  batch.set(doc(db, 'users', uid), userUpdate, { merge: true })

  await batch.commit()

  return { teamId }
}

/**
 * Switch current team for the signed-in user.
 * Usage: switchTeam(teamId)  // uses current auth user
 *        switchTeam(teamId, { uid, name, logoUrl }) // optional metadata
 */
export async function switchTeam(teamId, opts = {}) {
  const uid = opts.uid ?? getAuth().currentUser?.uid
  if (!uid) throw new Error('Not signed in')
  const meta =
    opts.name || opts.logoUrl
      ? { currentTeam: { id: teamId, name: opts.name ?? null, logoUrl: opts.logoUrl ?? '' } }
      : { currentTeam: { id: teamId } }
  await setDoc(
    doc(db, 'users', uid),
    { ...meta, updatedAt: serverTimestamp() },
    { merge: true }
  )
}

export async function unarchiveTeam(teamId) {
  await updateDoc(doc(db, 'teams', teamId), {
    isArchived: false,
    archived: false,
    archivedReason: null,
    updatedAt: serverTimestamp(),
  })
}

/**
 * Archives a team. If cascade is true, callers should implement additional clean-up
 * (closing scrimmages, declining interests, etc.) before calling this.
 */
export async function archiveTeamSafely(teamId, { cascade = false, reason } = {}) {
  await updateDoc(doc(db, 'teams', teamId), {
    isArchived: true,
    archived: true,
    archivedReason: reason || null,
    updatedAt: serverTimestamp(),
  })
  // In a future iteration, we can move cascade logic here.
  return { ok: true }
}

/**
 * Hard-delete a team, but ONLY if it's "empty" — no players, no events,
 * no scrimmages, just the owner. Intended for the "I just created this
 * by accident" case. Anything more populated should use archiveTeamSafely
 * so we keep history.
 *
 * Why guard the empty check client-side instead of dropping straight to
 * deleteDoc? Because Firestore rules will let an owner delete the team
 * doc, but deleting it without cleaning up `teams/{id}/members/*` and
 * `teams/{id}/fields/*` leaves orphan subcollection docs. Cascading
 * properly client-side is fine for empty teams; for populated teams we
 * want a Cloud Function (TODO) so we can reliably scrub registrations,
 * chats, scrimmages, and parent links too.
 *
 * Returns:
 *   { ok: true }                  — deleted
 *   { ok: false, reason: 'has_data', counts: {...} } — refuses, caller should suggest archive
 */
export async function deleteTeamIfEmpty(teamId, { uid } = {}) {
  if (!teamId) throw new Error('deleteTeamIfEmpty: teamId required')

  const teamSnap = await getDoc(doc(db, 'teams', teamId))
  if (!teamSnap.exists()) return { ok: false, reason: 'not_found' }
  const team = teamSnap.data()

  // Owner gate (defense in depth — Firestore rules also enforce this).
  const callerUid = uid || getAuth().currentUser?.uid
  const ownerUid = team.ownerId || team.coachId
  if (!callerUid || callerUid !== ownerUid) {
    return { ok: false, reason: 'not_owner' }
  }

  // Count anything that would be orphaned by a hard delete. Each of
  // these is a small read; we run them in parallel to keep latency low.
  // Players live as a subcollection of teams (teams/{teamId}/players),
  // not a top-level collection. Pre-fix this queried /players and 403'd.
  const [playersSnap, eventsSnap, scrimmagesSnap, membersSnap] = await Promise.all([
    getDocs(collection(db, 'teams', teamId, 'players')).catch(() => ({ size: 0 })),
    getDocs(query(collection(db, 'events'), where('teamId', '==', teamId))).catch(() => ({ size: 0 })),
    getDocs(query(collection(db, 'scrimmages'), where('teamId', '==', teamId))).catch(() => ({ size: 0 })),
    getDocs(collection(db, 'teams', teamId, 'members')).catch(() => ({ size: 0, docs: [] })),
  ])

  const counts = {
    players: playersSnap.size || 0,
    events: eventsSnap.size || 0,
    scrimmages: scrimmagesSnap.size || 0,
    // members > 1 means someone other than the owner has joined — that's
    // not "empty" anymore and we should bail.
    extraMembers: Math.max(0, (membersSnap.size || 0) - 1),
  }

  const hasData =
    counts.players > 0 ||
    counts.events > 0 ||
    counts.scrimmages > 0 ||
    counts.extraMembers > 0

  if (hasData) {
    return { ok: false, reason: 'has_data', counts }
  }

  // Best-effort cleanup of subcollections we know about. Failures here
  // are non-fatal — the team doc is what really matters for visibility,
  // and cloud function cleanup can sweep stragglers later.
  try {
    const memberDocs = membersSnap.docs || []
    await Promise.all(memberDocs.map((d) => deleteDoc(d.ref)))
  } catch (e) {
    console.warn('[deleteTeamIfEmpty] members cleanup failed', e)
  }

  try {
    const fieldsSnap = await getDocs(collection(db, 'teams', teamId, 'fields'))
    await Promise.all(fieldsSnap.docs.map((d) => deleteDoc(d.ref)))
  } catch (e) {
    console.warn('[deleteTeamIfEmpty] fields cleanup failed', e)
  }

  // Delete the team doc itself.
  await deleteDoc(doc(db, 'teams', teamId))

  // Clear the user's pointers if they still reference this team.
  if (callerUid) {
    try {
      const userRef = doc(db, 'users', callerUid)
      const userSnap = await getDoc(userRef)
      if (userSnap.exists()) {
        const u = userSnap.data() || {}
        const patch = {}
        if (u.currentTeam?.id === teamId) patch.currentTeam = null
        if (u.teamId === teamId) patch.teamId = null
        if (u.mustCompleteTeamId === teamId) patch.mustCompleteTeamId = null
        if (Object.keys(patch).length) {
          patch.updatedAt = serverTimestamp()
          await setDoc(userRef, patch, { merge: true })
        }
      }
    } catch (e) {
      console.warn('[deleteTeamIfEmpty] user pointer cleanup failed', e)
    }
  }

  return { ok: true }
}

export async function getTeamUsage(teamId) {
  const usage = {
    scrimmagesOpen: 0,
    scrimmagesConfirmed: 0,
    interestsActive: 0,
    chats: 0,
    usersLinked: 0,
  }
  try {
    const q1 = query(
      collection(db, 'scrimmages'),
      where('teamId', '==', teamId),
      where('status', '==', 'open')
    )
    usage.scrimmagesOpen = (await getDocs(q1)).size
  } catch {}

  try {
    const q2 = query(
      collection(db, 'scrimmages'),
      where('teamId', '==', teamId),
      where('status', '==', 'confirmed')
    )
    usage.scrimmagesConfirmed = (await getDocs(q2)).size
  } catch {}

  // Leave these as zero unless you add collections/queries for them
  return usage
}

/**
 * List all teams a user belongs to.
 * Primary: query /teams for coachId==uid OR staffIds contains uid (merge both).
 * Fallback: collectionGroup('members') to discover teamIds, then fetch docs.
 */
export async function getUserTeams(uid) {
  if (!uid) throw new Error('getUserTeams: uid is required')

  // 1) Direct queries (owned + staff)
  const teamsCol = collection(db, 'teams')
  const ownedQ = query(teamsCol, where('coachId', '==', uid))
  const staffQ = query(teamsCol, where('staffIds', 'array-contains', uid))

  const [ownedSnap, staffSnap] = await Promise.all([getDocs(ownedQ), getDocs(staffQ)])

  const dedup = new Map()
  ownedSnap.forEach((d) => dedup.set(d.id, { id: d.id, ...d.data() }))
  staffSnap.forEach((d) => dedup.set(d.id, { id: d.id, ...d.data() }))

  let results = Array.from(dedup.values())

  // 2) Fallback: membership subcollection (covers legacy data without staffIds)
  if (results.length === 0) {
    try {
      const cg = query(collectionGroup(db, 'members'), where(documentId(), '==', uid))
      const memSnaps = await getDocs(cg)
      const teamIds = Array.from(
        new Set(memSnaps.docs.map((d) => d.ref.parent?.parent?.id).filter(Boolean))
      )
      if (teamIds.length) {
        const fetched = await Promise.all(
          teamIds.map(async (id) => {
            const s = await getDoc(doc(db, 'teams', id))
            return s.exists() ? { id, ...s.data() } : null
          })
        )
        results = fetched.filter(Boolean)
      }
    } catch {}
  }

  return results.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''))
}

/* ------------------------- Membership & roles (NEW) ---------------------- */

/**
 * Look up a user by email (supports either `emailLower` or `email` schemas).
 * Returns { id, ...userData } or null.
 */
export async function findUserByEmail(email) {
  const e = String(email || '').trim().toLowerCase()
  if (!e) return null
  const q1 = query(collection(db, 'users'), where('emailLower', '==', e))
  const q2 = query(collection(db, 'users'), where('email', '==', e))
  const [r1, r2] = await Promise.allSettled([getDocs(q1), getDocs(q2)])
  const snap =
    (r1.status === 'fulfilled' && r1.value && !r1.value.empty && r1.value) ||
    (r2.status === 'fulfilled' && r2.value && !r2.value.empty && r2.value) ||
    null
  if (!snap) return null
  const d = snap.docs[0]
  return { id: d.id, ...d.data() }
}

/** Get a user document by UID. Returns { id, ...userData } or null. */
export async function getUserByUid(uid) {
  const ref = doc(db, 'users', uid)
  const s = await getDoc(ref)
  return s.exists() ? { id: s.id, ...s.data() } : null
}

/**
 * Grant team staff role: creates/merges teams/{teamId}/members/{userId}.
 * Writes the canonical `staff` role (legacy docs may still contain
 * `team_manager` / `manager` / `assistant` / `coach` — those are folded
 * onto TEAM_ROLES.STAFF by normalizeTeamRole at read time).
 *
 * The function name is kept for back-compat with existing call sites.
 */
export async function grantTeamManager({ teamId, userId, coachId }) {
  if (!teamId || !userId) throw new Error('grantTeamManager: teamId and userId required')

  const mref = doc(db, 'teams', teamId, 'members', userId)
  const user = await getUserByUid(userId).catch(() => null)

  const payload = {
    role: TEAM_ROLES.STAFF,
    status: 'active',
    grantedBy: coachId || null,
    grantedAt: serverTimestamp(),
    // nice-to-have display fields for your members list
    displayName: user?.displayName || user?.name || null,
    photoURL: user?.photoURL || null,
    email: user?.email || user?.emailLower || null,
  }

  await setDoc(mref, payload, { merge: true })
  return { ok: true }
}

/** List members for a team (lightweight helper for UI). */
export async function listTeamMembers(teamId) {
  const snap = await getDocs(collection(db, 'teams', teamId, 'members'))
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

/**
 * Does this role string belong on team.staffIds[]?
 *
 * We delegate to `normalizeTeamRole` so any legacy spelling that normalizes
 * to a canonical team role (`owner` / `staff`) gets mirrored correctly.
 * `member` (no match) and any unknown role return false and get pulled off.
 * Kept in sync with functions/modules/coachBroadcast.js and
 * src/services/inviteService.js so "can this person broadcast / manage the
 * team" stays consistent across client + server + invite-acceptance.
 */
function isStaffRole(role) {
  return normalizeTeamRole(role) !== null
}

/**
 * Update a member's role AND mirror the membership to team.staffIds[] so
 * the staff-contains query in getUserTeams / coachBroadcast path-2 stays
 * accurate. Non-staff roles (e.g. 'member') get pulled out of staffIds.
 */
export async function updateTeamMemberRole(teamId, userId, role) {
  if (!teamId || !userId || !role) throw new Error('teamId, userId, role required')
  await setDoc(doc(db, 'teams', teamId, 'members', userId), {
    role,
    updatedAt: serverTimestamp(),
  }, { merge: true })

  // Mirror to team.staffIds[]. We use updateDoc (not setDoc+merge) because
  // arrayUnion/arrayRemove only work through updateDoc's field transforms.
  try {
    const teamRef = doc(db, 'teams', teamId)
    if (isStaffRole(role)) {
      await updateDoc(teamRef, {
        staffIds: arrayUnion(userId),
        updatedAt: serverTimestamp(),
      })
    } else {
      await updateDoc(teamRef, {
        staffIds: arrayRemove(userId),
        updatedAt: serverTimestamp(),
      })
    }
  } catch (err) {
    // If the team doc doesn't exist yet or staffIds is missing, the member
    // write above is still the source of truth — don't fail the whole call.
    console.warn('[updateTeamMemberRole] staffIds mirror failed', err?.message)
  }

  return { ok: true }
}

/**
 * Remove a member from a team — deletes the members doc AND pulls the uid
 * out of team.staffIds[] so they disappear from "my teams" lists.
 */
export async function removeTeamMember(teamId, userId) {
  if (!teamId || !userId) throw new Error('teamId and userId required')
  await deleteDoc(doc(db, 'teams', teamId, 'members', userId))
  try {
    await updateDoc(doc(db, 'teams', teamId), {
      staffIds: arrayRemove(userId),
      updatedAt: serverTimestamp(),
    })
  } catch (err) {
    console.warn('[removeTeamMember] staffIds cleanup failed', err?.message)
  }
  return { ok: true }
}

/**
 * Subscribe to live team-members changes. Returns an unsubscribe function.
 * Shape per member: { id: uid, role, status, displayName?, email?, ... }.
 */
export function onTeamMembers(teamId, callback) {
  if (!teamId) throw new Error('onTeamMembers: teamId required')
  return onSnapshot(collection(db, 'teams', teamId, 'members'), (snap) => {
    callback(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
  })
}

/* ----------------------- Matching preferences (NEW) ---------------------- */
/**
 * Storage layout:
 *   teams/{teamId}/settings/matching  (single doc)
 * Shape is flexible; we provide defaults below for convenience.
 */

const matchingDocRef = (teamId) => doc(db, 'teams', teamId, 'settings', 'matching')

export const DEFAULT_MATCHING_PREFS = {
  radiusKm: 40,
  ageGroups: [], // e.g., ['2014','2015']
  weekdays: [1, 2, 3, 4, 5], // 1=Mon ... 7=Sun
  timeWindows: [{ start: '17:00', end: '20:00' }], // local time strings
  travel: { maxKm: 40, willingToHost: true, willingToTravel: true },
  skill: { min: 1, max: 5 },
  ai: { enabled: true },
  updatedAt: null,
}

/**
 * Read matching preferences. Set {withDefaults:true} to overlay sensible defaults.
 */
export async function getMatchingPreferences(teamId, { withDefaults = true } = {}) {
  if (!teamId) throw new Error('teamId is required')
  const snap = await getDoc(matchingDocRef(teamId))
  const data = snap.exists() ? snap.data() : {}
  return withDefaults ? { ...DEFAULT_MATCHING_PREFS, ...data } : data
}

/**
 * Update (merge) matching preferences. Pass only the fields you’re changing.
 * Example:
 *   await updateMatchingPreferences(teamId, { radiusKm: 50, ai: { enabled: false } })
 */
export async function updateMatchingPreferences(teamId, partialPrefs) {
  if (!teamId) throw new Error('teamId is required')
  if (!partialPrefs || typeof partialPrefs !== 'object') {
    throw new Error('partialPrefs must be an object')
  }
  await setDoc(
    matchingDocRef(teamId),
    { ...partialPrefs, updatedAt: serverTimestamp() },
    { merge: true }
  )
  return true
}

/**
 * Subscribe to live changes in matching preferences.
 * Returns an unsubscribe function.
 */
export function onMatchingPreferences(teamId, callback) {
  if (!teamId) throw new Error('teamId is required')
  return onSnapshot(matchingDocRef(teamId), (snap) => {
    callback(
      snap.exists()
        ? { ...DEFAULT_MATCHING_PREFS, ...snap.data() }
        : { ...DEFAULT_MATCHING_PREFS }
    )
  })
}
